import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Phone, MapPin, Shield, FileText, Home, ArrowRight, Menu, X } from 'lucide-react';
import SEOHead from './components/SEOHead';
import TrackingScripts from './components/TrackingScripts';
import VisitTracker from './components/VisitTracker';
import CookieConsent from './components/CookieConsent';
import QuoteForm, { PhoneStrip } from './components/QuoteForm';
import PhoneLink, { PHONES } from './components/PhoneLink';
import ThankYouPage from './pages/ThankYouPage';
import PrivacyPage from './pages/PrivacyPage';
import CookiesPage from './pages/CookiesPage';

// Lazy load AdminApp for better performance
const AdminApp = lazy(() => import('./admin/AdminApp'));

// Logo URLs
const LOGO_URL = "https://customer-assets.emergentagent.com/job_schatter-vlaanderen/artifacts/q7cigof0_logo_uwerkendschatter_A.svg";
const LOGO_DARK_URL = "https://customer-assets.emergentagent.com/job_schatter-vlaanderen/artifacts/r8raffa2_logo_uwerkendschatter_donker.svg";

// Team photos (swapped)
const TEAM_PHOTOS = {
  gino: "https://customer-assets.emergentagent.com/job_2b587252-1bba-4119-988d-864c9ff4f814/artifacts/c1hg38fi_IMG_1963.jpg",
  bjorn: "https://customer-assets.emergentagent.com/job_2b587252-1bba-4119-988d-864c9ff4f814/artifacts/mor02397_IMG_1981.jpg"
};

// Logo URLs
const VLABEL_LOGO = "https://customer-assets.emergentagent.com/job_schatter-vlaanderen/artifacts/vkutihkn_1e1cff1f-4748-4ed2-82ff-e9a0bbfd7a2b.png";
const BIV_LOGO = "https://customer-assets.emergentagent.com/job_schatter-vlaanderen/artifacts/8gt78ugs_images.png";

// Team Data
const TEAM = [
  {
    name: "Gino Haeck",
    initials: "GH",
    biv: "517.441",
    vlabel: "005304763177",
    regio: "Vlabel nummer: 005304763177",
    photo: TEAM_PHOTOS.gino,
    phone: PHONES.gino,
    email: "gino@uwerkendschatter.be",
    color: "avatar-gino",
    quote: "U belt mij. Niet een callcenter. Ik volg uw dossier persoonlijk op."
  },
  {
    name: "Bjorn Marievoet",
    initials: "BM",
    biv: "515.679",
    vlabel: "005304749538",
    regio: "Vlabel nummer: 005304749538",
    photo: TEAM_PHOTOS.bjorn,
    phone: PHONES.bjorn,
    email: "bjorn@uwerkendschatter.be",
    color: "avatar-bjorn",
    quote: "Erfenisdossiers zijn emotioneel zwaar. Ik werk discreet, snel en zonder oordeel."
  }
];

// Trusted Banks
const BANKS = ["KBC", "BNP Paribas Fortis", "ING", "Belfius", "Crelan"];

// FAQ Data
const FAQ_DATA = [
  {
    q: "Wat is een VLABEL-schatting en waarom heb ik die nodig?",
    a: "Bij een erfenis in Vlaanderen bent u wettelijk verplicht om de waarde van onroerend goed te laten schatten door een VLABEL-erkende schatter. Dit verslag dient als basis voor de berekening van de erfbelasting. Zonder erkend verslag riskeert u boetes en vertragingen."
  },
  {
    q: "Verschilt een VLABEL-schatting van een gewoon schattingsverslag?",
    a: "Ja. Een VLABEL-schatting voldoet aan specifieke wettelijke eisen van de Vlaamse Belastingdienst en mag enkel worden opgesteld door een erkende schatter. Een gewoon schattingsverslag is bedoeld voor banken, verzekeringen of verkoop."
  },
  {
    q: "Mijn notaris vraagt dringend een VLABEL-schatting — hoe snel kan dit?",
    a: "Wij leveren standaard binnen 5 werkdagen. Bij uitzonderlijke spoed is levering binnen 48 uur mogelijk. Neem contact op voor de mogelijkheden."
  },
  {
    q: "Aanvaardt mijn bank dit schattingsverslag?",
    a: "Ja. Onze verslagen worden aanvaard door alle grote Belgische banken waaronder KBC, BNP Paribas Fortis, ING, Belfius en Crelan."
  },
  {
    q: "Kan ik een VLABEL-schatting combineren met een plaatsbeschrijving?",
    a: "Absoluut. Bij een erfenis met een huurpand kunt u beide diensten combineren. U ontvangt dan één gecombineerde offerte met korting."
  },
  {
    q: "Werkt u in heel Vlaanderen?",
    a: "Ja, wij bedienen alle vijf Vlaamse provincies: Oost-Vlaanderen, West-Vlaanderen, Antwerpen, Vlaams-Brabant en Limburg."
  }
];

// ============================================
// URGENCY BAR
// ============================================
const UrgencyBar = () => {
  // Dynamic number that changes weekly (based on week number)
  const getAvailableSlots = () => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const weekNumber = Math.ceil(((now - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
    // Returns a number between 2 and 5 based on week number
    return ((weekNumber % 4) + 2);
  };

  const availableSlots = getAvailableSlots();

  return (
    <div className="bg-[var(--navy)] text-white py-2.5 px-4 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-sm">
        <span className="bg-[var(--amber)] text-white px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide">
          Deze week
        </span>
        <span className="text-[rgba(199,223,240,0.9)]">
          Nog <strong className="text-white">{availableSlots}</strong> plaatsbezoeken beschikbaar
        </span>
      </div>
    </div>
  );
};

// ============================================
// NAVIGATION
// ============================================
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsOpen(false);
  };

  return (
    <nav className="bg-[var(--cream)] border-b border-[var(--card-brd)] sticky top-[42px] z-40">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src={LOGO_URL} 
              alt="uwerkendschatter.be" 
              className="h-16 md:h-20 w-auto"
              style={{ background: 'transparent' }}
            />
          </div>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('werkwijze')} className="link-hover text-[var(--ink-md)] hover:text-[var(--ink)] text-sm font-medium">
              Werkwijze
            </button>
            <button onClick={() => scrollToSection('diensten')} className="link-hover text-[var(--ink-md)] hover:text-[var(--ink)] text-sm font-medium">
              Diensten
            </button>
            <button onClick={() => scrollToSection('team')} className="link-hover text-[var(--ink-md)] hover:text-[var(--ink)] text-sm font-medium">
              Team
            </button>
            <button 
              onClick={() => scrollToSection('aanvraag')} 
              className="btn-primary !py-2.5 !px-5 !text-[13px]"
            >
              Gratis offerte
            </button>
            <PhoneLink phone={PHONES.gino} locatie="header" className="flex items-center gap-1.5 text-[var(--ink)] hover:text-[var(--amber)] text-sm font-semibold transition-colors">
              <Phone className="w-4 h-4 text-[var(--amber)]" /> Bel {PHONES.gino.display}
            </PhoneLink>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setIsOpen(!isOpen)} className="md:hidden p-2">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-[var(--card-brd)]">
            <div className="flex flex-col gap-3">
              <button onClick={() => scrollToSection('werkwijze')} className="text-left py-2 text-[var(--ink-md)]">Werkwijze</button>
              <button onClick={() => scrollToSection('diensten')} className="text-left py-2 text-[var(--ink-md)]">Diensten</button>
              <button onClick={() => scrollToSection('team')} className="text-left py-2 text-[var(--ink-md)]">Team</button>
              <button onClick={() => scrollToSection('aanvraag')} className="btn-primary mt-2">Gratis offerte</button>
              <PhoneLink phone={PHONES.gino} locatie="header-mobile" className="flex items-center justify-center gap-2 py-3 text-[var(--ink)] font-semibold">
                <Phone className="w-4 h-4 text-[var(--amber)]" /> Bel {PHONES.gino.display}
              </PhoneLink>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// ============================================
// HERO SECTION
// ============================================
const HeroSection = () => {
  return (
    <section className="bg-[var(--navy)] relative overflow-hidden">
      {/* Decorative gradients */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(200,135,42,0.15)_0%,transparent_70%)] pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[radial-gradient(circle,rgba(26,90,60,0.10)_0%,transparent_70%)] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20 relative z-10">
        <div className="grid md:grid-cols-[1fr_420px] gap-10 md:gap-16 items-start">
          {/* Left Column - Content */}
          <div className="space-y-6">
            {/* Tag Pills */}
            <div className="flex flex-wrap gap-2">
              {['Erfenis', 'Echtscheiding', 'Bankdossier'].map(tag => (
                <span key={tag} className="px-3 py-1.5 rounded-full text-xs font-medium text-white/80 bg-white/[0.07] border border-white/20">
                  {tag}
                </span>
              ))}
            </div>

            {/* H1 */}
            <h1 className="font-display text-[clamp(2rem,5vw,3rem)] font-medium text-[var(--cream)] leading-tight">
              Uw dossier staat stil.<br />
              <span className="text-[var(--amber-lt)]">Eén erkend verslag</span> lost het op.
            </h1>

            {/* Subheading */}
            <p className="font-display text-xl md:text-2xl italic text-[var(--amber-lt)]">
              In 5 werkdagen. Gegarandeerd.
            </p>

            {/* Body */}
            <p className="text-[rgba(199,223,240,0.75)] max-w-lg leading-relaxed">
              Of het nu een erfenis is die blokkeert, een bank die wacht of een scheiding die niet sluit — 
              uw notaris, bank en rechtbank aanvaarden uitsluitend een verslag van een beëdigd, erkend expert.
            </p>

            {/* VLABEL Box */}
            <div className="bg-[rgba(26,90,60,0.15)] border border-[var(--green-brd)] rounded-[var(--r-lg)] p-4 flex gap-4">
              <div className="text-3xl">🏛️</div>
              <div>
                <p className="text-[var(--green-lt)] font-medium text-sm">VLABEL-erkend</p>
                <p className="text-[rgba(199,223,240,0.8)] text-sm leading-relaxed">
                  Voor erfenisaangiftes bij de Vlaamse Belastingdienst is uitsluitend een VLABEL-erkende schatter toegestaan. 
                  Wij zijn dat — en wij leveren het verslag dat uw notaris nodig heeft.
                </p>
              </div>
            </div>

            {/* Mini Team Card */}
            <div className="bg-white/[0.08] backdrop-blur border border-white/10 rounded-[var(--r-lg)] p-4">
              <div className="flex items-center gap-4">
                <div className="flex -space-x-3">
                  <img src={TEAM[0].photo} alt={TEAM[0].name} className="w-12 h-12 rounded-full border-2 border-white object-cover" />
                  <img src={TEAM[1].photo} alt={TEAM[1].name} className="w-12 h-12 rounded-full border-2 border-white object-cover" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">{TEAM[0].name} & {TEAM[1].name}</p>
                  <div className="flex gap-2 mt-1">
                    <span className="vlabel-badge !bg-[var(--green-bg)] !text-[var(--green-lt)] !border-[var(--green-brd)]">VLABEL</span>
                    <span className="text-xs text-white/60">BIV erkend</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Checkmarks */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              {['Reactie binnen 24 uur', 'Vaste prijs vooraf', 'Levering in 5 werkdagen', 'Heel Vlaanderen'].map(item => (
                <div key={item} className="flex items-center gap-2">
                  <span className="text-[var(--amber-lt)]">✓</span>
                  <span className="text-[rgba(199,223,240,0.8)]">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="md:sticky md:top-[120px]">
            <QuoteForm />
            <PhoneStrip />
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// VLABEL SECTION
// ============================================
const VlabelSection = () => {
  return (
    <section className="py-16 md:py-24 bg-[var(--cream)]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="border-2 border-[var(--green-brd)] rounded-[var(--r-xl)] p-8 md:p-12 relative overflow-hidden">
          {/* Decorative gradient */}
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[radial-gradient(circle,rgba(26,90,60,0.08)_0%,transparent_70%)] pointer-events-none"></div>

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="vlabel-badge">VLABEL</span>
              <h2 className="font-display text-2xl md:text-3xl text-[var(--green)]">Waarom VLABEL-erkenning?</h2>
            </div>
            <p className="text-[var(--ink-md)] max-w-2xl mb-10">
              Bij een erfenis in Vlaanderen bent u wettelijk verplicht de waarde van onroerend goed te laten schatten 
              door een erkende VLABEL-schatter. Niet elke vastgoedexpert heeft deze erkenning.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Comparison */}
              <div className="space-y-3">
                <h4 className="font-semibold text-[var(--ink)] mb-4">Zonder VLABEL-erkenning</h4>
                <div className="compare-item no">
                  <span>✕</span>
                  <span>Verslag geweigerd door Vlaamse Belastingdienst</span>
                </div>
                <div className="compare-item no">
                  <span>✕</span>
                  <span>Boete bij onjuiste waardering</span>
                </div>
                <div className="compare-item no">
                  <span>✕</span>
                  <span>Notaris kan niet verder</span>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold text-[var(--ink)] mb-4">Met VLABEL-erkenning</h4>
                <div className="compare-item yes">
                  <span>✓</span>
                  <span>Wettelijk aanvaard door VLABEL</span>
                </div>
                <div className="compare-item yes">
                  <span>✓</span>
                  <span>Bescherming tegen boetes</span>
                </div>
                <div className="compare-item yes">
                  <span>✓</span>
                  <span>Dossier wordt direct verwerkt</span>
                </div>
              </div>
            </div>

            {/* Logos */}
            <div className="flex items-center gap-6 mt-10 pt-6 border-t border-[var(--green-brd)]">
              <img src={VLABEL_LOGO} alt="VLABEL erkend" className="h-16 w-auto" />
              <img src={BIV_LOGO} alt="BIV erkend" className="h-12 w-auto" style={{ mixBlendMode: 'multiply' }} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// TEAM SECTION
// ============================================
const TeamSection = () => {
  return (
    <section id="team" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="text-xs font-semibold uppercase tracking-widest text-[var(--amber)] mb-2 block">Uw experts</span>
          <h2 className="font-display text-3xl md:text-4xl text-[var(--ink)]">Twee namen. Persoonlijke opvolging.</h2>
          <p className="text-[var(--ink-md)] mt-3 max-w-xl mx-auto">
            Geen anoniem kantoor. U weet altijd wie uw dossier behandelt.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {TEAM.map((member, index) => (
            <div key={index} className="team-card">
              <div className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <img src={member.photo} alt={member.name} className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-md" />
                  <div>
                    <h3 className="font-display text-xl text-[var(--ink)]">{member.name}</h3>
                    <p className="text-sm text-[var(--ink-lt)]">Beëdigd Vastgoedexpert</p>
                    <span className="vlabel-badge mt-2">VLABEL</span>
                  </div>
                </div>

                <div className="space-y-2 text-sm mb-4">
                  <div className="flex items-center gap-2 text-[var(--ink-md)]">
                    <Shield className="w-4 h-4 text-[var(--amber)]" />
                    <span>BIV erkend: {member.biv}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[var(--ink-md)]">
                    <MapPin className="w-4 h-4 text-[var(--amber)]" />
                    <span>{member.regio}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[var(--ink-md)]">
                    <Phone className="w-4 h-4 text-[var(--amber)]" />
                    <PhoneLink phone={member.phone} locatie="team" className="hover:text-[var(--amber)] transition-colors" />
                  </div>
                </div>

                <blockquote className="border-l-3 border-[var(--amber)] pl-4 py-2 bg-[var(--cream)] rounded-r-lg">
                  <p className="font-display italic text-[var(--ink-md)]">"{member.quote}"</p>
                </blockquote>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Bar */}
        <div className="bg-[var(--navy)] rounded-[var(--r-lg)] mt-12 p-6 md:p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { num: '250+', label: 'Schattingen per jaar' },
              { num: '24u', label: 'Contact binnen' },
              { num: '5 dagen', label: 'Standaard levertijd' },
              { num: '5', label: 'Vlaamse provincies' }
            ].map((stat, i) => (
              <div key={i}>
                <p className="font-display text-3xl md:text-4xl text-[var(--amber-lt)]">{stat.num}</p>
                <p className="text-sm text-[rgba(199,223,240,0.7)] mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// TRUST BAR
// ============================================
const TrustBar = () => (
  <section className="py-10 bg-[var(--cream-dk)]">
    <div className="max-w-7xl mx-auto px-4 md:px-6">
      <p className="text-center text-xs font-semibold uppercase tracking-widest text-[var(--ink-lt)] mb-6">
        Aanvaard door alle grote banken
      </p>
      <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
        {BANKS.map(bank => (
          <span key={bank} className="text-[var(--ink-md)] font-bold text-lg">{bank}</span>
        ))}
        <span className="vlabel-badge">VLABEL</span>
      </div>
    </div>
  </section>
);

// ============================================
// PROCESS SECTION
// ============================================
const ProcessSection = () => {
  const steps = [
    { num: 1, title: 'Offerte', desc: 'Vrijblijvende prijsopgave binnen 24 uur' },
    { num: 2, title: 'Plaatsbezoek', desc: 'Grondige inspectie van het pand' },
    { num: 3, title: 'Analyse', desc: 'Marktvergelijking & waardebepaling' },
    { num: 4, title: 'Verslag', desc: 'Officieel document binnen 5 werkdagen', isActive: true }
  ];

  return (
    <section id="werkwijze" className="py-16 md:py-24 bg-[var(--cream)]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="text-xs font-semibold uppercase tracking-widest text-[var(--amber)] mb-2 block">Werkwijze</span>
          <h2 className="font-display text-3xl md:text-4xl text-[var(--ink)]">Van aanvraag tot verslag</h2>
        </div>

        <div className="relative">
          {/* Connection line */}
          <div className="hidden md:block absolute top-[18px] left-[15%] right-[15%] h-[2px] bg-gradient-to-r from-[var(--amber)] to-[var(--amber-lt)]"></div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="text-center relative">
                <div className={`step-number mx-auto mb-4 ${step.isActive ? 'active' : ''}`}>
                  {step.num}
                </div>
                <h4 className="font-semibold text-[var(--ink)] mb-1">{step.title}</h4>
                <p className="text-sm text-[var(--ink-lt)]">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// SERVICES SECTION
// ============================================
const ServicesSection = () => {
  const services = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: 'Schattingsverslag',
      desc: 'Voor bank, notaris of rechtbank. Erkend door alle grote financiële instellingen.',
      features: ['BIV-erkend', 'Alle banken', 'Juridisch waterdicht']
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'VLABEL-schatting',
      desc: 'Wettelijk verplicht bij erfenis in Vlaanderen. Aanvaard door de Vlaamse Belastingdienst.',
      features: ['VLABEL-erkend', 'Erfbelasting', 'Notarieel aanvaard'],
      isHighlight: true
    },
    {
      icon: <Home className="w-8 h-8" />,
      title: 'Plaatsbeschrijving',
      desc: 'Bij intrede of uittrede van een huurpand. Beschermt huurder én verhuurder.',
      features: ['Intrede/uittrede', 'Fotodossier', 'Juridische zekerheid']
    }
  ];

  return (
    <section id="diensten" className="py-16 md:py-24 bg-gradient-to-b from-[var(--navy)] to-[var(--navy-lt)]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="text-xs font-semibold uppercase tracking-widest text-[var(--amber-lt)] mb-2 block">Diensten</span>
          <h2 className="font-display text-3xl md:text-4xl text-[var(--cream)]">Drie officiële diensten</h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {services.map((service, i) => (
            <div 
              key={i} 
              className={`bg-white/[0.06] border ${service.isHighlight ? 'border-[var(--amber)]' : 'border-white/10'} rounded-[var(--r-lg)] p-6 hover:bg-white/[0.08] hover:border-[var(--amber)] hover:-translate-y-1 transition-all`}
            >
              <div className={`text-${service.isHighlight ? '[var(--amber)]' : '[var(--amber-lt)]'} mb-4`}>
                {service.icon}
              </div>
              <h3 className="font-display text-xl text-white mb-2">{service.title}</h3>
              <p className="text-[rgba(199,223,240,0.7)] text-sm mb-4">{service.desc}</p>
              <div className="space-y-2">
                {service.features.map((feat, j) => (
                  <div key={j} className="flex items-center gap-2 text-sm text-[rgba(199,223,240,0.6)]">
                    <span className="text-[var(--amber-lt)]">✓</span>
                    <span>{feat}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <button 
            onClick={() => document.getElementById('aanvraag')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-primary"
          >
            Gratis offerte aanvragen <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

// ============================================
// FAQ SECTION
// ============================================
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className="py-16 md:py-24 bg-[var(--cream)]">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="text-xs font-semibold uppercase tracking-widest text-[var(--amber)] mb-2 block">FAQ</span>
          <h2 className="font-display text-3xl md:text-4xl text-[var(--ink)]">Veelgestelde vragen</h2>
        </div>

        <div className="space-y-0">
          {FAQ_DATA.map((faq, i) => (
            <div key={i} className="faq-item">
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full py-5 flex items-center justify-between text-left"
              >
                <span className="font-medium text-[var(--ink)] pr-4">{faq.q}</span>
                <span className={`faq-icon text-[var(--amber)] text-2xl ${openIndex === i ? 'open' : ''}`}>+</span>
              </button>
              {openIndex === i && (
                <div className="pb-5 text-[var(--ink-md)] leading-relaxed">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// CLOSING CTA
// ============================================
const ClosingCTA = () => {
  // Dynamic number that changes weekly (based on week number)
  const getAvailableSlots = () => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const weekNumber = Math.ceil(((now - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
    return ((weekNumber % 4) + 2);
  };

  const availableSlots = getAvailableSlots();

  return (
    <section className="py-16 md:py-24 bg-[var(--navy)] relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[radial-gradient(circle,rgba(200,135,42,0.15)_0%,transparent_70%)] pointer-events-none"></div>
      
      <div className="max-w-4xl mx-auto px-4 md:px-6 text-center relative z-10">
        <span className="text-[10px] font-bold uppercase tracking-[2px] text-[var(--amber-lt)] mb-4 block">
          Start vandaag
        </span>
        <h2 className="font-display text-3xl md:text-4xl text-[var(--cream)] mb-4">
          Uw dossier kan morgen al in beweging zijn.
        </h2>
        <p className="text-[rgba(199,223,240,0.7)] mb-8 max-w-xl mx-auto">
          Nog {availableSlots} plaatsbezoeken beschikbaar deze week. Vraag nu een vrijblijvende offerte aan.
        </p>

        <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-10">
          {TEAM.map((member, i) => (
            <div key={i} className="flex items-center gap-4">
              <img src={member.photo} alt={member.name} className="w-14 h-14 rounded-full border-2 border-white/20 object-cover" />
              <div className="text-left">
                <p className="text-white font-medium">{member.name}</p>
                <PhoneLink phone={member.phone} locatie="cta" className="text-[rgba(199,223,240,0.6)] text-sm hover:text-white transition-colors" />
              </div>
            </div>
          ))}
        </div>

        <button 
          onClick={() => document.getElementById('aanvraag')?.scrollIntoView({ behavior: 'smooth' })}
          className="btn-primary text-lg !px-8 !py-4"
        >
          Gratis offerte aanvragen <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </section>
  );
};

// ============================================
// FOOTER
// ============================================
const Footer = () => (
  <footer className="bg-[var(--navy-md)] py-10">
    <div className="max-w-7xl mx-auto px-4 md:px-6">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center">
          <img 
            src={LOGO_DARK_URL} 
            alt="uwerkendschatter.be" 
            className="h-12 w-auto"
            style={{ background: 'transparent' }}
          />
        </div>

        <div className="flex flex-wrap justify-center gap-4 text-xs text-[rgba(199,223,240,0.5)]">
          <span>BIV {TEAM[0].biv} & {TEAM[1].biv}</span>
          <span>|</span>
          <span>VLABEL erkend</span>
          <span>|</span>
          <span>© 2025</span>
        </div>

        <div className="flex items-center gap-4">
          <img src={VLABEL_LOGO} alt="VLABEL" className="h-8 w-auto opacity-70" />
          <img src={BIV_LOGO} alt="BIV" className="h-6 w-auto opacity-70" style={{ mixBlendMode: 'multiply', filter: 'brightness(2)' }} />
        </div>
      </div>

      {/* Phone numbers */}
      <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-[rgba(199,223,240,0.7)]">
        <span className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-[var(--amber-lt)]" />
          Gino <PhoneLink phone={PHONES.gino} locatie="footer" className="text-white hover:text-[var(--amber-lt)] transition-colors" />
        </span>
        <span className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-[var(--amber-lt)]" />
          Bjorn <PhoneLink phone={PHONES.bjorn} locatie="footer" className="text-white hover:text-[var(--amber-lt)] transition-colors" />
        </span>
      </div>

      {/* Privacy links */}
      <div className="mt-6 pt-6 border-t border-[rgba(199,223,240,0.1)] flex flex-wrap justify-center gap-4 text-xs text-[rgba(199,223,240,0.5)]">
        <a href="/privacy" className="hover:text-[#C8963E] transition-colors">Privacybeleid</a>
        <span>|</span>
        <a href="/cookies" className="hover:text-[#C8963E] transition-colors">Cookiebeleid</a>
        <span>|</span>
        <button 
          onClick={() => window.openCookieSettings?.()}
          className="hover:text-[#C8963E] transition-colors"
        >
          Cookie-instellingen
        </button>
      </div>
    </div>
  </footer>
);

// ============================================
// LANDING PAGE
// ============================================
const LandingPage = () => {
  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );

    document.querySelectorAll('.fade-up').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      <UrgencyBar />
      <Navigation />
      <HeroSection />
      <VlabelSection />
      <TeamSection />
      <TrustBar />
      <ProcessSection />
      <ServicesSection />
      <FAQSection />
      <ClosingCTA />
      <Footer />
    </div>
  );
};

// ============================================
// ERROR PAGES
// ============================================
const NotFoundPage = () => (
  <div className="min-h-screen bg-[var(--cream)] flex items-center justify-center px-4">
    <div className="text-center max-w-md">
      <div className="text-8xl font-display text-[var(--amber)] mb-4">404</div>
      <h1 className="text-2xl font-display text-[var(--ink)] mb-4">Pagina niet gevonden</h1>
      <p className="text-[var(--ink-md)] mb-8">
        De pagina die u zoekt bestaat niet of is verplaatst.
      </p>
      <a href="/" className="btn-primary inline-flex items-center gap-2">
        <Home className="w-4 h-4" /> Terug naar home
      </a>
    </div>
  </div>
);

const ErrorPage = () => (
  <div className="min-h-screen bg-[var(--cream)] flex items-center justify-center px-4">
    <div className="text-center max-w-md">
      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <X className="w-10 h-10 text-red-500" />
      </div>
      <h1 className="text-2xl font-display text-[var(--ink)] mb-4">Er ging iets mis</h1>
      <p className="text-[var(--ink-md)] mb-8">
        We ondervinden technische problemen. Probeer het later opnieuw.
      </p>
      <a href="/" className="btn-primary inline-flex items-center gap-2">
        <Home className="w-4 h-4" /> Terug naar home
      </a>
    </div>
  </div>
);

// ============================================
// APP
// ============================================

// Redirect component for login subdomain
const LoginSubdomainRedirect = ({ children }) => {
  const hostname = window.location.hostname;
  
  // If accessing via login.uwerkendschatter.be, redirect to /admin
  if (hostname === 'login.uwerkendschatter.be' && window.location.pathname === '/') {
    window.location.href = '/admin';
    return null;
  }
  
  return children;
};

function App() {
  const [cookieConsent, setCookieConsent] = useState(null);
  
  // Check if we're on the login subdomain
  const isLoginSubdomain = window.location.hostname === 'login.uwerkendschatter.be';

  return (
    <Router>
      <SEOHead />
      <TrackingScripts consent={cookieConsent} />
      {/* Don't track visits on login subdomain (admin only) */}
      {!isLoginSubdomain && <VisitTracker />}
      <CookieConsent onConsentChange={setCookieConsent} />
      <Routes>
        {/* On login subdomain, redirect root to admin */}
        <Route path="/" element={
          isLoginSubdomain ? (
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#F5F0E8]"><div className="text-[#C8963E]">Laden...</div></div>}>
              <AdminApp />
            </Suspense>
          ) : (
            <LandingPage />
          )
        } />
        <Route path="/bedankt" element={<ThankYouPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/admin/*" element={
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#F5F0E8]"><div className="text-[#C8963E]">Laden...</div></div>}>
            <AdminApp />
          </Suspense>
        } />
        <Route path="/error" element={<ErrorPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
  );
}

export default App;
